import { useEffect, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { useFrame, useThree } from '@react-three/fiber'
import { useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import { createInstancedAssetGroup } from '../../../utils/instancedAsset'
import {
    lanePositions,
    powerUpScaleStartDistance,
    powerUpScaleEndDistance,
} from '../../config/constants'
import type { TrackController } from '../track/trackController'
import { useRunState } from '../../provider/useRunState'
import { buildPowerUpCollisionData, checkPowerUpCollision } from './powerUpCollision'
import { useTrackController } from '../track/useTrackController'
import { preparePowerUpAsset, powerUpColorByType, createGlowBillboard, getDustSpriteTexture } from './powerUpAssets'
import type { PowerUpType } from '@/src/stores/runStateTypes'
import { untracked } from 'mobx'
import { POWER_UP_CONFIG } from './powerUpConfig'
import { gltfAsset } from '../../assetStore'
import { useAtlasMaterial } from '../../materials/atlasMaterial'
import { usePowerUpTextures, type PowerUpTextures } from './powerUpTextures'
import {
    getOrbPowerUpPlacements,
    resolvePowerUpYOffset,
    type IndexedPowerUpPlacement,
} from './powerUpHelpers'
import { disposeObject, markForDisposal } from '../../../utils/threeDisposal'
import { vibrate } from '@/src/lib/vibration'

const {
    baseScale: BASE_SCALE,
    orbScale: ORB_SCALE,
    orbYOffset: ORB_Y_OFFSET,
    idleRotationSpeed: IDLE_ROTATION_SPEED,
    idleBobAmplitude: IDLE_BOB_AMPLITUDE,
    idleBobSpeed: IDLE_BOB_SPEED,
    idleTrackRotationOffset: IDLE_TRACK_ROTATION_OFFSET,
    collectDuration: COLLECT_DURATION,
    glowPlaneYOffset: GLOW_PLANE_Y_OFFSET,
    glowPlaneSize: GLOW_PLANE_SIZE,
} = POWER_UP_CONFIG

const GROUND_COLLECT_THRESHOLD = 0.3

const PARTICLE_SETTINGS = {
    count: POWER_UP_CONFIG.particleCount,
    size: POWER_UP_CONFIG.particleSize,
    opacity: POWER_UP_CONFIG.particleOpacity,
    speed: POWER_UP_CONFIG.particleSpeed,
    spread: POWER_UP_CONFIG.particleSpread,
    lifetime: POWER_UP_CONFIG.particleLifetime,
    upwardBias: POWER_UP_CONFIG.particleUpwardBias,
    drag: POWER_UP_CONFIG.particleDrag,
}
const ORB_COLLECT_DURATION = COLLECT_DURATION * 0.35
const ORB_IDLE_ROTATION_OFFSET = THREE.MathUtils.degToRad(90)
const ORB_IDLE_ROTATION_SWING = THREE.MathUtils.degToRad(45)
const POWER_UP_TYPES = ['battery', 'plane', 'shield'] as const
const POWER_UP_TYPE_PHASE_OFFSET: Record<(typeof POWER_UP_TYPES)[number], number> = {
    battery: 0,
    plane: (Math.PI * 2) / 3,
    shield: (Math.PI * 4) / 3,
}

type PowerUpInstance = {
    type: PowerUpType
    position: number
    worldPosition: THREE.Vector3
    worldRotation: THREE.Quaternion
    collectTimer: number
    glowPlane: THREE.Mesh
}

type ParticleBurst = {
    age: number
    points: THREE.Points
    geometry: THREE.BufferGeometry
    material: THREE.PointsMaterial
    positions: Float32Array
    velocities: Float32Array
}

type FlashSphere = {
    age: number
    mesh: THREE.Mesh
    material: THREE.MeshBasicMaterial
    duration: number
    maxScale: number
}

type PowerUpsController = {
    group: THREE.Group
    instances: PowerUpInstance[]
    instancedMeshesByType: Map<PowerUpType, THREE.InstancedMesh[]>
    orbMeshesByType: Map<PowerUpType, THREE.InstancedMesh[]>
    instanceIndexesByType: Map<PowerUpType, number[]>
    spawnCollectParticles: (instanceIndex: number, color: string) => void
    update: (trackPosition: number, delta: number, cameraQuaternion: THREE.Quaternion) => void
}

export const PowerUpOrbs = observer(function PowerUpOrbs() {
    const trackController = useTrackController()
    const { scene } = useGLTF(gltfAsset.Powerup)
    const runState = useRunState()
    const { camera } = useThree()
    const atlasMaterial = useAtlasMaterial()
    const textures = usePowerUpTextures()

    const orbPlacements = useMemo(() => getOrbPowerUpPlacements(), [])

    const collisionData = useMemo(
        () => buildPowerUpCollisionData(orbPlacements, trackController),
        [orbPlacements, trackController],
    )

    const { group, instances, spawnCollectParticles, update } = useMemo(
        () => createPowerUps(scene, trackController, orbPlacements, atlasMaterial, textures),
        [scene, trackController, orbPlacements, atlasMaterial, textures],
    )
    useEffect(() => {
        return () => {
            disposeObject(group)
        }
    }, [group])

    useEffect(() => {
        update(
            trackController.getTrackPlaybackState().trackPosition,
            0,
            camera.quaternion,
        )
    }, [update, trackController, camera])

    // respawn every orb whenever a reset/restart bumps the token.
    const collectiblesResetToken = runState.collectiblesResetToken
    useEffect(() => {
        collisionData.powerUps.forEach((powerUp) => {
            powerUp.collected = false
        })
        instances.forEach((instance) => {
            instance.collectTimer = -1
        })
    }, [collectiblesResetToken, collisionData, instances])

    useFrame((_, delta) => {
        const trackPosition = trackController.getTrackPlaybackState().trackPosition

        update(trackPosition, delta, camera.quaternion)

        if (runState.phase !== 'running') return
        if (runState.snapshot.hitReactionTimer > 0) return

        const snapshot = runState.snapshot

        const hitIndex = checkPowerUpCollision(
            trackPosition,
            snapshot.laneVisualX,
            collisionData,
        )

        if (hitIndex === -1) return

        const placement = orbPlacements[hitIndex]

        if (placement.type === 'heart' || placement.type === 'coin') return
        if (placement.type === 'shield' && !runState.canCollectShield()) return

        const jumpVisualOffset = snapshot.characterVisualPosition[1]
        const isGroundLevel = jumpVisualOffset < GROUND_COLLECT_THRESHOLD

        if (Boolean(placement.up) === isGroundLevel) return

        untracked(() => {
            collisionData.powerUps[hitIndex].collected = true
            instances[hitIndex].collectTimer = 0
            spawnCollectParticles(hitIndex, powerUpColorByType[placement.type])
        })

        runState.activatePowerUp(placement.type)
        vibrate([40, 70, 200])
        window.dispatchEvent(new Event('powerup-collect'))
    })

    return <primitive object={group} />
})

function createPowerUps(
    sourceAsset: THREE.Object3D,
    trackController: TrackController,
    placements: IndexedPowerUpPlacement[],
    atlasMaterial: THREE.MeshBasicMaterial,
    textures: PowerUpTextures,
): PowerUpsController {

    const yRotation = new THREE.Quaternion()
    const finalRotation = new THREE.Quaternion()
    const Y_AXIS = new THREE.Vector3(0, 1, 0)
    const _bobbedPosition = new THREE.Vector3()
    const _localYOffset = new THREE.Vector3()
    const _particleDir = new THREE.Vector3()
    let idleElapsed = 0

    const rootGroup = new THREE.Group()

    const instances: PowerUpInstance[] = placements.map((placement) => {
        const frame = trackController.frameAtPosition(placement.position)
        const rotation = frame.cameraQuaternion.clone()
        const offset = new THREE.Vector3(
            lanePositions[placement.track],
            resolvePowerUpYOffset(placement.up),
            0,
        ).applyQuaternion(rotation)

        const worldPosition = frame.position.clone().add(offset)

        const glowPlane = createGlowBillboard(powerUpColorByType[placement.type])
        glowPlane.position.copy(worldPosition)
        glowPlane.scale.setScalar(0)
        rootGroup.add(glowPlane)

        return {
            type: placement.type,
            position: placement.position,
            worldPosition,
            worldRotation: rotation,
            collectTimer: -1,
            glowPlane,
        }
    })

    const instancedMeshesByType = new Map<PowerUpType, THREE.InstancedMesh[]>()
    const orbMeshesByType = new Map<PowerUpType, THREE.InstancedMesh[]>()
    const instanceIndexesByType = new Map<PowerUpType, number[]>()
    const matrix = new THREE.Matrix4()
    const scale = new THREE.Vector3()
    const _glowOffset = new THREE.Vector3()

    for (const type of POWER_UP_TYPES) {
        const indicesForType = placements.flatMap((p, i) =>
            p.type === type ? [i] : [],
        )
        if (indicesForType.length === 0) continue

        const preparedAsset = preparePowerUpAsset(
            sourceAsset,
            type,
            atlasMaterial,
            textures,
        )

        const initialZeroMatrices = indicesForType.map((globalIndex) =>
            new THREE.Matrix4().compose(
                instances[globalIndex].worldPosition,
                instances[globalIndex].worldRotation,
                new THREE.Vector3(0, 0, 0),
            ),
        )

        const baseAsset = preparedAsset.clone(true)
        const sphereInBase = baseAsset.getObjectByName('PowerUpSphere')
        if (sphereInBase) sphereInBase.removeFromParent()

        const baseGroup = createInstancedAssetGroup(baseAsset, initialZeroMatrices)
        const baseMeshes = baseGroup.children.filter(
            (child): child is THREE.InstancedMesh =>
                child instanceof THREE.InstancedMesh,
        )

        const orbAsset = new THREE.Group()
        const sphereSource = preparedAsset.getObjectByName('PowerUpSphere')
        if (sphereSource) {
            const sphereClone = sphereSource.clone(true)
            const glowInOrb = sphereClone.getObjectByName('GlowBillboard')
            if (glowInOrb) glowInOrb.removeFromParent()
            orbAsset.add(sphereClone)
        }

        const orbGroup = createInstancedAssetGroup(orbAsset, initialZeroMatrices)
        const orbMeshes = orbGroup.children.filter(
            (child): child is THREE.InstancedMesh =>
                child instanceof THREE.InstancedMesh,
        )

        rootGroup.add(baseGroup)
        rootGroup.add(orbGroup)
        instancedMeshesByType.set(type, baseMeshes)
        orbMeshesByType.set(type, orbMeshes)
        instanceIndexesByType.set(type, indicesForType)
    }

    const activeBursts: ParticleBurst[] = []

    const activeFlashSpheres: FlashSphere[] = []

    function spawnFlashSphere(position: THREE.Vector3, color: string) {
        const geometry = new THREE.SphereGeometry(1, 10, 10)
        markForDisposal(geometry)
        const material = new THREE.MeshBasicMaterial({
            color,
            transparent: true,
            opacity: 0.85,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
            side: THREE.FrontSide,
            toneMapped: false,
        })
        markForDisposal(material)
        const mesh = new THREE.Mesh(geometry, material)
        mesh.position.copy(position)
        mesh.scale.setScalar(0.01)
        rootGroup.add(mesh)
        activeFlashSpheres.push({ age: 0, mesh, material, duration: 0.35, maxScale: 1 })
    }

    function spawnCollectParticles(instanceIndex: number, color: string) {
        const instance = instances[instanceIndex]
        const idlePhaseOffset = POWER_UP_TYPE_PHASE_OFFSET[instance.type as keyof typeof POWER_UP_TYPE_PHASE_OFFSET] ?? 0

        const ORB_WORLD_HEIGHT = 2.3713 * BASE_SCALE
        const bobOffset =
            Math.sin(idleElapsed * IDLE_BOB_SPEED + idlePhaseOffset) * IDLE_BOB_AMPLITUDE
        const orbPosition = instance.worldPosition.clone()
        orbPosition.add(
            _localYOffset
                .set(0, ORB_WORLD_HEIGHT + bobOffset, 0)
                .applyQuaternion(instance.worldRotation),
        )

        const count = Math.max(1, Math.floor(PARTICLE_SETTINGS.count))
        const positions = new Float32Array(count * 3)
        const velocities = new Float32Array(count * 3)
        const geometry = new THREE.BufferGeometry()
        markForDisposal(geometry)
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))

        for (let i = 0; i < count; i++) {
            const i3 = i * 3
            _particleDir
                .set(
                    THREE.MathUtils.randFloatSpread(2),
                    Math.random() + PARTICLE_SETTINGS.upwardBias,
                    THREE.MathUtils.randFloatSpread(2),
                )
                .normalize()
                .multiplyScalar(
                    THREE.MathUtils.randFloat(0.4, 1.0) * PARTICLE_SETTINGS.spread
                )
            velocities[i3] = _particleDir.x
            velocities[i3 + 1] = _particleDir.y
            velocities[i3 + 2] = _particleDir.z
        }

        const material = new THREE.PointsMaterial({
            color,
            size: PARTICLE_SETTINGS.size,
            map: getDustSpriteTexture(),
            transparent: true,
            opacity: PARTICLE_SETTINGS.opacity,
            depthWrite: false,
            blending: THREE.NormalBlending,
            sizeAttenuation: true,
            toneMapped: false,
        })
        markForDisposal(material)

        const points = new THREE.Points(geometry, material)
        points.position.copy(orbPosition)
        rootGroup.add(points)

        activeBursts.push({ age: 0, points, geometry, material, positions, velocities })
        // Lichtkugel an Orb-Position spawnen
        const flashPos = instance.worldPosition.clone()
        flashPos.add(
            _localYOffset
                .set(0, ORB_WORLD_HEIGHT, 0)
                .applyQuaternion(instance.worldRotation),
        )
        spawnFlashSphere(flashPos, color)
    }

    return {
        group: rootGroup,
        instances,
        instancedMeshesByType,
        orbMeshesByType,
        instanceIndexesByType,
        spawnCollectParticles,
        update(trackPosition: number, delta: number, cameraQuaternion: THREE.Quaternion) {
            idleElapsed += delta

            for (let i = activeBursts.length - 1; i >= 0; i--) {
                const burst = activeBursts[i]
                burst.age += delta
                const t = burst.age / PARTICLE_SETTINGS.lifetime

                if (t >= 1) {
                    rootGroup.remove(burst.points)
                    burst.geometry.dispose()
                    burst.material.dispose()
                    activeBursts.splice(i, 1)
                    continue
                }

                const drag = Math.exp(-PARTICLE_SETTINGS.drag * delta)
                for (let p = 0; p < burst.positions.length; p += 3) {
                    burst.positions[p] += burst.velocities[p] * PARTICLE_SETTINGS.speed * delta
                    burst.positions[p + 1] += burst.velocities[p + 1] * PARTICLE_SETTINGS.speed * delta
                    burst.positions[p + 2] += burst.velocities[p + 2] * PARTICLE_SETTINGS.speed * delta
                    burst.velocities[p] *= drag
                    burst.velocities[p + 1] *= drag
                    burst.velocities[p + 2] *= drag
                }

                burst.geometry.attributes.position.needsUpdate = true
                burst.material.opacity = PARTICLE_SETTINGS.opacity * (1 - t)
                burst.material.size = PARTICLE_SETTINGS.size
            }

            // FlashSpheres updaten
            for (let i = activeFlashSpheres.length - 1; i >= 0; i--) {
                const flash = activeFlashSpheres[i]
                flash.age += delta
                const t = flash.age / flash.duration
                if (t >= 1) {
                    rootGroup.remove(flash.mesh)
                    flash.mesh.geometry.dispose()
                    flash.material.dispose()
                    activeFlashSpheres.splice(i, 1)
                    continue
                }
                // Ease-out cubic: schnell groß, dann langsam
                const scaleT = 1 - Math.pow(1 - t, 3)
                flash.mesh.scale.setScalar(flash.maxScale * scaleT)
                // Opacity: erst kurz halten, dann schnell faden
                flash.material.opacity = 0.85 * Math.pow(1 - t, 1.5)
            }

            for (const type of POWER_UP_TYPES) {
                const baseMeshes = instancedMeshesByType.get(type) ?? []
                const orbMeshes = orbMeshesByType.get(type) ?? []
                const globalIndices = instanceIndexesByType.get(type) ?? []
                const idlePhaseOffset = POWER_UP_TYPE_PHASE_OFFSET[type]
                const idleBobOffset =
                    (Math.sin(idleElapsed * IDLE_BOB_SPEED + idlePhaseOffset) * IDLE_BOB_AMPLITUDE) + ORB_Y_OFFSET

                globalIndices.forEach((globalIndex, typeIndex) => {
                    const instance = instances[globalIndex]
                    const idleRotation =
                        ORB_IDLE_ROTATION_OFFSET +
                        Math.sin(
                            idleElapsed * IDLE_ROTATION_SPEED +
                            idlePhaseOffset -
                            instance.position * IDLE_TRACK_ROTATION_OFFSET,
                        ) * ORB_IDLE_ROTATION_SWING

                    // baseScale: normales Scale-Fade — bleibt immer sichtbar
                    // auch während und nach dem Einsammeln
                    const delta_track = trackController.positionDeltaOnTrack(
                        instance.position,
                        trackPosition,
                    )
                    const blend = THREE.MathUtils.inverseLerp(
                        powerUpScaleStartDistance,
                        powerUpScaleEndDistance,
                        delta_track,
                    )
                    const baseScale = THREE.MathUtils.smoothstep(blend, 0, 1) * BASE_SCALE

                    if (instance.collectTimer === -1 && baseScale <= 0) {
                        matrix.compose(
                            instance.worldPosition,
                            instance.worldRotation,
                            scale.setScalar(0),
                        )
                        baseMeshes.forEach((mesh) => mesh.setMatrixAt(typeIndex, matrix))
                        orbMeshes.forEach((mesh) => mesh.setMatrixAt(typeIndex, matrix))
                        instance.glowPlane.visible = false
                        return
                    }

                    let orbScale = baseScale * ORB_SCALE
                    if (instance.collectTimer === -2) {
                        orbScale = 0
                    } else if (instance.collectTimer >= 0) {
                        instance.collectTimer += delta
                        const progress = instance.collectTimer / ORB_COLLECT_DURATION
                        if (progress >= 1.0) {
                            instance.collectTimer = -2
                        }
                        orbScale = 0
                    }

                    if (instance.collectTimer === -1) {
                        yRotation.setFromAxisAngle(Y_AXIS, idleRotation)
                    }

                    // ── Sockel ──
                    finalRotation.copy(instance.worldRotation)
                    matrix.compose(instance.worldPosition, finalRotation, scale.setScalar(baseScale))
                    baseMeshes.forEach((mesh) => mesh.setMatrixAt(typeIndex, matrix))

                    // ── Orb ──
                    const ORB_MODEL_Y = 2.3713 * BASE_SCALE
                    if (instance.collectTimer === -1) {
                        _bobbedPosition.copy(instance.worldPosition)
                        _bobbedPosition.add(
                            _localYOffset
                                .set(0, idleBobOffset, 0)
                                .applyQuaternion(instance.worldRotation),
                        )
                        finalRotation.copy(instance.worldRotation).multiply(yRotation)
                        matrix.compose(_bobbedPosition, finalRotation, scale.setScalar(orbScale))
                    } else if (instance.collectTimer >= 0) {
                        const scaleRatio = orbScale / BASE_SCALE
                        _bobbedPosition.copy(instance.worldPosition)
                        _bobbedPosition.add(
                            _localYOffset
                                .set(0, ORB_MODEL_Y * (1 - scaleRatio), 0)
                                .applyQuaternion(instance.worldRotation),
                        )
                        finalRotation.copy(instance.worldRotation).multiply(yRotation)
                        matrix.compose(_bobbedPosition, finalRotation, scale.setScalar(orbScale))
                    } else {
                        finalRotation.copy(instance.worldRotation)
                        matrix.compose(instance.worldPosition, finalRotation, scale.setScalar(0))
                    }
                    orbMeshes.forEach((mesh) => mesh.setMatrixAt(typeIndex, matrix))

                    // ── Glow ──
                    if (instance.collectTimer >= 0) {
                        const progress = instance.collectTimer / ORB_COLLECT_DURATION
                        if (progress < 0.4) {
                            const t = progress / 0.4
                            instance.glowPlane.visible = true
                            instance.glowPlane.position.copy(instance.worldPosition)
                            instance.glowPlane.quaternion.copy(cameraQuaternion)
                            instance.glowPlane.scale.setScalar(GLOW_PLANE_SIZE * (1 + t * 3))
                            const mat = instance.glowPlane.material as THREE.MeshBasicMaterial
                            mat.opacity = 0.5 * (1 - t)
                        } else {
                            instance.glowPlane.visible = false
                        }
                    } else if (instance.collectTimer === -2) {
                        instance.glowPlane.visible = false
                    } else {
                        const scaleRatio = baseScale / BASE_SCALE
                        const bobOffset = idleBobOffset
                        const totalYOffset = GLOW_PLANE_Y_OFFSET * scaleRatio + bobOffset
                        _glowOffset.set(0, totalYOffset, 0).applyQuaternion(instance.worldRotation)
                        instance.glowPlane.visible = baseScale > 0
                        instance.glowPlane.position.copy(instance.worldPosition).add(_glowOffset)
                        instance.glowPlane.quaternion.copy(cameraQuaternion)
                        instance.glowPlane.scale.setScalar(scaleRatio * GLOW_PLANE_SIZE)
                    }
                    baseMeshes.forEach((mesh) => { mesh.instanceMatrix.needsUpdate = true })
                    orbMeshes.forEach((mesh) => { mesh.instanceMatrix.needsUpdate = true })
                })

            }
        },
    }
}
